import React from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  SaktiPaginationMeta,
  SaktiRkhHarvesterListItem,
  SaktiRkhListItem,
  SaktiRkhTabKey,
} from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiRkhTabsTableProps {
  loading: boolean;
  activeTab: SaktiRkhTabKey;
  rkhRows: SaktiRkhListItem[];
  harvesterRows: SaktiRkhHarvesterListItem[];
  rkhPagination: SaktiPaginationMeta;
  harvesterPagination: SaktiPaginationMeta;
  onTabChange: (tab: SaktiRkhTabKey) => void;
  onPageChange: (page: number) => void;
  onViewDetail: (rkhId: string) => void;
}

const getStatusColorScheme = (status: string) => {
  if (status === 'COMPLETE') return 'green';
  if (status === 'PRESENCE') return 'orange';
  return 'gray';
};

const getPaginationButtons = (currentPage: number, totalPages: number) => {
  const visibleRange = 3;
  const startPage = Math.max(1, currentPage - 1);
  const endPage = Math.min(totalPages, startPage + visibleRange - 1);
  const pages: number[] = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }
  return pages;
};

const SaktiRkhTabsTable: React.FC<SaktiRkhTabsTableProps> = ({
  loading,
  activeTab,
  rkhRows,
  harvesterRows,
  rkhPagination,
  harvesterPagination,
  onTabChange,
  onPageChange,
  onViewDetail,
}) => {
  const isRkhTab = activeTab === 'rkh';
  const rows = isRkhTab ? rkhRows : harvesterRows;
  const pagination = isRkhTab ? rkhPagination : harvesterPagination;
  const pageButtons = getPaginationButtons(pagination.page, pagination.total_pages);

  return (
    <VStack align="stretch" spacing={0}>
      <HStack spacing={2} borderBottomWidth="1px" borderBottomColor="customGreen.500" mb={3} overflowX="auto">
        <Button
          size="sm"
          variant="ghost"
          borderBottomWidth="3px"
          borderBottomColor={isRkhTab ? 'customGreen.500' : 'transparent'}
          borderRadius={0}
          color={isRkhTab ? 'customGreen.500' : 'gray.700'}
          fontSize={SAKTI_UI_SIZE.tabTitle}
          fontWeight="bold"
          onClick={() => onTabChange('rkh')}
          minW="max-content"
        >
          Daftar RKH
        </Button>
        <Button
          size="sm"
          variant="ghost"
          borderBottomWidth="3px"
          borderBottomColor={!isRkhTab ? 'customGreen.500' : 'transparent'}
          borderRadius={0}
          color={!isRkhTab ? 'customGreen.500' : 'gray.700'}
          fontSize={SAKTI_UI_SIZE.tabTitle}
          fontWeight="bold"
          onClick={() => onTabChange('harvesters')}
          minW="max-content"
        >
          Daftar Pemanen
        </Button>
      </HStack>

      <Skeleton isLoaded={!loading} borderRadius={SAKTI_UI_SIZE.cardRadius}>
        <Box
          borderRadius={SAKTI_UI_SIZE.cardRadius}
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
        >
          {/* Desktop Table */}
          <Box overflowX="auto" display={{ base: 'none', lg: 'block' }}>
            <Table variant="simple">
              <Thead bg="customGreen.500">
                <Tr>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>RKH Number</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Tanggal RKH</Th>
                  {!isRkhTab && <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Pemanen</Th>}
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Estate</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Divisi</Th>
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Date Sync</Th>
                  {isRkhTab && <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Status</Th>}
                  <Th color="white" fontSize={SAKTI_UI_SIZE.tableHeaderFont} py={2.5}>Detail</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.length === 0 ? (
                  <Tr>
                    <Td colSpan={isRkhTab ? 7 : 8}>
                      <Text color="gray.500" textAlign="center" py={4} fontSize="sm">
                        Tidak ada data untuk filter saat ini
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  rows.map((row) => {
                    const rkhId = isRkhTab
                      ? (row as SaktiRkhListItem).id
                      : (row as SaktiRkhHarvesterListItem).rkh_id;
                    return (
                      <Tr key={isRkhTab ? (row as SaktiRkhListItem).id : (row as SaktiRkhHarvesterListItem).harvester_id}>
                        <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{row.rkh_nbr}</Td>
                        <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{dayjs(row.rkh_date).format('DD/MM/YYYY')}</Td>
                        {!isRkhTab && <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{(row as SaktiRkhHarvesterListItem).harvester_name}</Td>}
                        <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{row.estate_name}</Td>
                        <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{row.division_name}</Td>
                        <Td fontSize={SAKTI_UI_SIZE.tableCellFont} py={2.5}>{dayjs(row.date_sync).format('DD/MM/YYYY HH:mm:ss')}</Td>
                        {isRkhTab && (
                          <Td py={2.5}>
                            <Badge fontSize="0.65rem" colorScheme={getStatusColorScheme((row as SaktiRkhListItem).status)}>
                              {(row as SaktiRkhListItem).status}
                            </Badge>
                          </Td>
                        )}
                        <Td py={2.5}>
                          <IconButton
                            aria-label="Lihat detail"
                            size="sm"
                            variant="ghost"
                            icon={<FiEye />}
                            onClick={() => onViewDetail(rkhId)}
                          />
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {/* Mobile Cards */}
          <Box display={{ base: 'block', lg: 'none' }}>
            {rows.length === 0 ? (
              <Box p={6} textAlign="center">
                <Text color="gray.500" fontSize="sm">
                  Tidak ada data untuk filter saat ini
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={0} divider={<Divider />}>
                {rows.map((row) => {
                  const rkhId = isRkhTab
                    ? (row as SaktiRkhListItem).id
                    : (row as SaktiRkhHarvesterListItem).rkh_id;
                  
                  return (
                    <Box key={isRkhTab ? (row as SaktiRkhListItem).id : (row as SaktiRkhHarvesterListItem).harvester_id} p={4} onClick={() => onViewDetail(rkhId)} _active={{ bg: 'gray.50' }}>
                      <HStack justify="space-between" mb={2}>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" fontSize="sm">{row.rkh_nbr}</Text>
                          <Text fontSize="xs" color="gray.500">{dayjs(row.rkh_date).format('DD/MM/YYYY')}</Text>
                        </VStack>
                        {isRkhTab && (
                          <Badge fontSize="0.6rem" colorScheme={getStatusColorScheme((row as SaktiRkhListItem).status)}>
                            {(row as SaktiRkhListItem).status}
                          </Badge>
                        )}
                      </HStack>

                      <VStack align="start" spacing={1}>
                        {!isRkhTab && (
                          <HStack spacing={1}>
                            <Text fontSize="xs" color="gray.500" w="60px">Pemanen:</Text>
                            <Text fontSize="xs" fontWeight="medium">{(row as SaktiRkhHarvesterListItem).harvester_name}</Text>
                          </HStack>
                        )}
                        <HStack spacing={1}>
                          <Text fontSize="xs" color="gray.500" w="60px">Unit:</Text>
                          <Text fontSize="xs">{row.estate_name} - {row.division_name}</Text>
                        </HStack>
                        <HStack spacing={1}>
                          <Text fontSize="xs" color="gray.500" w="60px">Sync:</Text>
                          <Text fontSize="xs">{dayjs(row.date_sync).format('DD/MM/YYYY HH:mm')}</Text>
                        </HStack>
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>

          <HStack
            px={4}
            py={3}
            bg="customGreen.500"
            color="white"
            justify="space-between"
            flexWrap="wrap"
            gap={3}
          >
            <Text fontSize="xs" fontWeight="semibold" letterSpacing="wider">
              SHOWING {rows.length} OF {pagination.total_items.toLocaleString('id-ID')}
            </Text>

            <HStack spacing={1}>
              <IconButton
                aria-label="Previous page"
                icon={<FiChevronLeft />}
                size="sm"
                variant="outline"
                borderColor="whiteAlpha.700"
                color="white"
                isDisabled={pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
              />

              {pageButtons.map((page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={page === pagination.page ? 'solid' : 'outline'}
                  bg={page === pagination.page ? 'white' : 'transparent'}
                  color={page === pagination.page ? 'customGreen.500' : 'white'}
                  borderColor="whiteAlpha.700"
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </Button>
              ))}

              <IconButton
                aria-label="Next page"
                icon={<FiChevronRight />}
                size="sm"
                variant="outline"
                borderColor="whiteAlpha.700"
                color="white"
                isDisabled={pagination.page >= pagination.total_pages}
                onClick={() => onPageChange(pagination.page + 1)}
              />
            </HStack>
          </HStack>
        </Box>
      </Skeleton>
    </VStack>
  );
};

export default SaktiRkhTabsTable;
